const normalizeServiceName = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const serviceNameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

const SERVICE_ARRAY_KEYS = new Set(["data", "serviceData", "servicesData"]);

const getServiceBaseName = (service) => service?.rawName || service?.name || "Unnamed";

const getSortableServiceName = (item) =>
  item?.serviceName || item?.name || item?.displayName || item?.rawName || "";

export const compareServiceNames = (left, right) =>
  serviceNameCollator.compare(getSortableServiceName(left), getSortableServiceName(right));

const isServiceReportItem = (item) => {
  if (!item || typeof item !== "object" || Array.isArray(item)) return false;
  if ("serviceName" in item || "serviceId" in item) return true;
  return (
    "name" in item &&
    (
      "agentsData" in item ||
      "arrivalTimes" in item ||
      "chart" in item ||
      "completed" in item ||
      "days" in item ||
      "max" in item ||
      "min" in item ||
      "total" in item ||
      "totalServiceData" in item ||
      "totalServiceTime" in item ||
      "waitingTimeInSeconds" in item
    )
  );
};

const shouldSortServiceArray = (items, key) =>
  SERVICE_ARRAY_KEYS.has(key) && items.some(isServiceReportItem);

const sortServiceArray = (items, key) =>
  shouldSortServiceArray(items, key) ? [...items].sort(compareServiceNames) : items;

const buildServicePath = (service, serviceById) => {
  const path = [];
  let current = service;
  const seen = new Set();

  while (current && current.id && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(getServiceBaseName(current));
    current = serviceById.get(current.parent);
  }

  return path;
};

const getQualifierForDepth = (path, depth) => {
  const parents = path.slice(0, -1).filter(Boolean);
  if (!parents.length) return "";
  return parents.slice(-depth).join(" > ");
};

export const buildShortestUniqueServiceNameMap = (services = []) => {
  const serviceById = new Map(services.map((service) => [service.id, service]));
  const serviceMeta = services.map((service) => ({
    service,
    baseName: getServiceBaseName(service),
    path: buildServicePath(service, serviceById),
  }));

  const groups = serviceMeta.reduce((acc, meta) => {
    const key = normalizeServiceName(meta.baseName);
    if (!acc.has(key)) acc.set(key, []);
    acc.get(key).push(meta);
    return acc;
  }, new Map());

  const displayNameById = new Map();

  groups.forEach((group) => {
    if (group.length === 1) {
      displayNameById.set(group[0].service.id, group[0].baseName);
      return;
    }

    group.forEach((meta) => {
      const parents = meta.path.slice(0, -1);
      let qualifier = "";

      for (let depth = 1; depth <= parents.length; depth += 1) {
        const candidate = getQualifierForDepth(meta.path, depth);
        const repeatsBaseName =
          normalizeServiceName(candidate) === normalizeServiceName(meta.baseName);
        const duplicateCount = group.filter(
          (other) => getQualifierForDepth(other.path, depth) === candidate
        ).length;

        if (candidate && !repeatsBaseName && duplicateCount === 1) {
          qualifier = candidate;
          break;
        }
      }

      if (!qualifier) {
        qualifier = parents.join(" > ") || meta.service.id?.slice(0, 8) || "Duplicate";
      }

      displayNameById.set(meta.service.id, `${meta.baseName} (${qualifier})`);
    });
  });

  return displayNameById;
};

export const decorateServicesWithDisplayNames = (services = []) => {
  const displayNameById = buildShortestUniqueServiceNameMap(services);
  const serviceById = new Map(services.map((service) => [service.id, service]));

  return services
    .map((service) => {
      const rawName = getServiceBaseName(service);
      const servicePath = buildServicePath(service, serviceById);
      const displayName = displayNameById.get(service.id) || rawName;

      return {
        ...service,
        rawName,
        displayName,
        name: displayName,
        servicePath,
      };
    })
    .sort(compareServiceNames);
};

export const resolveServiceName = (services = [], serviceId, fallbackName = "Unnamed") =>
  services.find((service) => service.id === serviceId)?.name || fallbackName;

const normalizeReportNode = (node, serviceById, inheritedServiceName = null, key = null) => {
  if (Array.isArray(node)) {
    return sortServiceArray(
      node.map((item) => normalizeReportNode(item, serviceById, inheritedServiceName)),
      key
    );
  }

  if (!node || typeof node !== "object") {
    return node;
  }

  const serviceId = node.serviceId || (node.id && serviceById.has(node.id) ? node.id : null);
  const matchedService = serviceId ? serviceById.get(serviceId) : null;
  const serviceName = matchedService?.name || inheritedServiceName;
  const nextNode = {};

  Object.entries(node).forEach(([key, value]) => {
    nextNode[key] = normalizeReportNode(value, serviceById, serviceName, key);
  });

  if (matchedService) {
    if ("serviceName" in nextNode && nextNode.serviceName !== matchedService.name) {
      nextNode.rawServiceName = nextNode.rawServiceName || nextNode.serviceName;
      nextNode.serviceName = matchedService.name;
    }

    if ("name" in nextNode && nextNode.name !== matchedService.name) {
      nextNode.rawName = nextNode.rawName || nextNode.name;
      nextNode.name = matchedService.name;
    }
  } else if (serviceName && "serviceName" in nextNode && nextNode.serviceName !== serviceName) {
    nextNode.rawServiceName = nextNode.rawServiceName || nextNode.serviceName;
    nextNode.serviceName = serviceName;
  }

  return nextNode;
};

export const normalizeReportServiceLabels = (reportsData = [], services = []) => {
  if (!Array.isArray(reportsData) || !reportsData.length) {
    return reportsData;
  }

  const serviceById = new Map(services.map((service) => [service.id, service]));
  return normalizeReportNode(reportsData, serviceById);
};
